import { createRootRouteWithContext } from '@tanstack/react-router';

import { createQueryLoader } from '@/loader';
import NotFoundPage from '@/pages/not-found/NotFoundPage';
import RootPageQueryNode, {
  type RootPageQuery,
} from '@/pages/root/__generated__/RootPageQuery.graphql';
import RootPage from '@/pages/root/RootPage';
import { getRelayEnvironment } from '@/relay/environment';
import styles from '@/styles.css?url';
import { buildRobotsContent, siteName } from '@/utils/seo';

type RouterContext = {
  relayEnvironment: ReturnType<typeof getRelayEnvironment>['environment'];
};

const loadRoot = createQueryLoader<RootPageQuery>(RootPageQueryNode, {
  fetchPolicy: 'store-or-network',
});

export const Route = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: NotFoundPage,
  async loader({ context, location }) {
    return {
      isReactManagedDirectorySeo:
        location.pathname.startsWith('/agents/') ||
        /^\/letting-agents\/[^/]+$/.test(location.pathname),
      root: await loadRoot({
        variables: {},
        environment: context.relayEnvironment,
      }),
    };
  },
  head: ({ loaderData }) => {
    const isReactManagedDirectorySeo =
      loaderData?.isReactManagedDirectorySeo ?? false;
    const defaultSeo = isReactManagedDirectorySeo
      ? []
      : [
          { title: 'Budgy — Is your letting agent still the best deal?' },
          {
            name: 'description',
            content:
              'We find landlords the best local letting agents, negotiate the best deal and handle the switch if it is worth it.',
          },
          { property: 'og:site_name', content: siteName },
          { property: 'og:locale', content: 'en_GB' },
          { name: 'robots', content: buildRobotsContent() },
        ];

    return {
      meta: [
        { charSet: 'utf-8' },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
        ...defaultSeo,
        {
          name: 'facebook-domain-verification',
          content: 'atqg6we0x5oilqcv7gqe3j5zqg8jm4',
        },
        { name: 'theme-color', content: '#fafaf8' },
      ],
      links: [
        { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
        { rel: 'stylesheet', href: styles },
      ],
    };
  },
  component: RootPage,
});
